import React from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import SelectMenu from "@kit/design/select-menu"
import { OldButton } from "@kit/design/old-button"
import Toggle from "@kit/design/toggle"
import Label from "@kit/design/label"
import Stack from "@kit/design/stack"
import Input from "@kit/design/input"
import Grid from "@kit/design/grid"
import { RadioButtonGroup, handleDelete } from "../setting-utils"
import { ColorInput } from "@kit/design/color-picker"
import { InputGroup } from "core/deprecated-inputs"
import { Field } from "core/form"
import { useSavedColors } from "hooks/use-saved-colors"

const MIN_LINK_FONT_SIZE = 10
const MAX_LINK_FONT_SIZE = 30

function PixelInput(props) {
  return (
    <InputGroup append="px">
      <Input {...props} />
    </InputGroup>
  )
}

export function LinksSettings({ block_id }) {
  const { properties, update, remove } = useBlocks(block_id)
  const usingCustomColors = properties.iconColor
  const savedColors = useSavedColors()

  function changeFontSize(newValue) {
    if (newValue > MAX_LINK_FONT_SIZE) {
      update({ fontSize: MAX_LINK_FONT_SIZE })
    } else if (newValue < MIN_LINK_FONT_SIZE) {
      update({ fontSize: MIN_LINK_FONT_SIZE })
    } else {
      update({ fontSize: newValue })
    }
  }

  function resetColors(e) {
    if (e.target.value === "platform") {
      update({ iconColor: null })
    } else {
      update({ iconColor: "#777777" })
    }
  }

  function removeLinks() {
    handleDelete(block_id, remove, "link collection")
  }

  return (
    <div className="px-4 pb-4">
      <Stack spacing={6}>
        <Grid columns="repeat(2, minmax(0, 1fr))" gap="1rem">
          <Field
            control={ColorInput}
            label="Background color"
            name="backgroundColor"
            onChange={color => update({ backgroundColor: color.hex })}
            value={properties.backgroundColor}
            savedColors={savedColors}
          />

          <Field
            control={ColorInput}
            label="Text color"
            name="textColor"
            onChange={color => update({ textColor: color.hex })}
            value={properties.textColor}
            savedColors={savedColors}
          />
        </Grid>

        <Grid columns="repeat(2, 1fr)" gap="1rem">
          <Field
            control={PixelInput}
            label="Font size"
            name="fontSize"
            onChange={e => update({ fontSize: e.target.value })}
            onBlur={e => changeFontSize(e.target.value)}
            min={MIN_LINK_FONT_SIZE}
            max={MAX_LINK_FONT_SIZE}
            value={properties.fontSize}
            type="number"
          />

          <Field
            control={SelectMenu}
            name="Font weight"
            label="Font weight"
            onChange={e => update({ fontWeight: e.target.value })}
            value={properties.fontWeight}
            options={[
              { value: "normal", label: "Normal" },
              { value: "bold", label: "Bold" }
            ]}
          />
        </Grid>

        <div>
          <Label>Border radius</Label>
          <RadioButtonGroup
            name="Border radius"
            keyName="borderRadius"
            value={properties.borderRadius}
            onChange={e => update({ borderRadius: e.target.value })}
            options={[
              { label: "None", value: 0 },
              { label: "Small", value: 4 },
              { label: "Large", value: 10 },
              { label: "Full", value: 50 }
            ]}
          />
        </div>

        <div>
          <Label>Border width</Label>
          <RadioButtonGroup
            name="Border width"
            keyName="borderWidth"
            value={properties.linkBorderWidth}
            onChange={e => update({ linkBorderWidth: e.target.value })}
            options={[
              { label: "None", value: 0 },
              { label: "Small", value: 1 },
              { label: "Medium", value: 3 },
              { label: "Large", value: 6 }
            ]}
          />
        </div>

        {Boolean(properties.linkBorderWidth) && (
          <div>
            <Label>Link border color</Label>
            <ColorInput
              value={properties.linkBorderColor}
              name="Link border color"
              onChange={color => update({ linkBorderColor: color.hex })}
              savedColors={savedColors}
            />
          </div>
        )}

        <div>
          <Grid columns="55px 1fr">
            <div className="flex items-center">
              <Toggle
                enabled={properties.useIcons}
                onClick={() => update({ useIcons: !properties.useIcons })}
              />
            </div>
            <Stack spacing={2} className="py-1">
              <div className="font-semibold text-gray-800 text-sm">
                Show icons
              </div>
            </Stack>
          </Grid>
        </div>

        {properties.useIcons && (
          <Grid columns="repeat(2, 1fr)" gap="1rem">
            <div>
              <div className="mb-1">
                <Label htmlFor="color_style">Icon color style</Label>
              </div>
              <SelectMenu
                label="Icon color style"
                id="color_style"
                onChange={resetColors}
                value={usingCustomColors ? "custom" : "platform"}
                className="w-full"
                options={[
                  { label: "Platform Color", value: "platform" },
                  { label: "Custom Color", value: "custom" }
                ]}
              />
            </div>

            {usingCustomColors && (
              <Field
                control={ColorInput}
                label="Icon color"
                name="iconColor"
                onChange={color => update({ iconColor: color.hex })}
                value={properties.iconColor}
                savedColors={savedColors}
              />
            )}
          </Grid>
        )}

        {properties.useIcons && (
          <div>
            <Label>Icon border width</Label>
            <RadioButtonGroup
              name="Icon border width"
              keyName="iconBorderWidth"
              value={properties.iconBorderWidth}
              onChange={e => update({ iconBorderWidth: e.target.value })}
              options={[
                { label: "None", value: 0 },
                { label: "Small", value: 1 },
                { label: "Medium", value: 3 },
                { label: "Large", value: 6 }
              ]}
            />
          </div>
        )}

        {properties.useIcons && Boolean(properties.iconBorderWidth) && (
          <div>
            <Label>Icon border color</Label>
            <ColorInput
              value={properties.iconBorderColor}
              name="Icon border color"
              onChange={color => update({ iconBorderColor: color.hex })}
              savedColors={savedColors}
            />
          </div>
        )}
        {!properties.disableRemoval && (
          <OldButton
            size="sm"
            is="secondary"
            className="ml-auto block"
            onClick={removeLinks}
            icon="delete"
          >
            Remove links
          </OldButton>
        )}
      </Stack>
    </div>
  )
}
